import React, { useEffect, useRef } from 'react';

const CONNECTION_DISTANCE = 140;
const NODE_COLOR = 'rgba(167, 139, 250, 0.65)';

/** Scale node count with section area so tall sections don't look sparse. */
function getNodeCount(width, height) {
  const area = width * height;
  return Math.min(85, Math.max(42, Math.round(area / 12000)));
}

export default function NeuralNetworkBackground() {
  const canvasRef = useRef(null);
  const stateRef = useRef({ nodes: [], width: 0, height: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const parent = canvas?.parentElement;
    if (!canvas || !parent) return undefined;

    const ctx = canvas.getContext('2d');
    let animationId;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const { width, height } = parent.getBoundingClientRect();

      if (width < 1 || height < 1) return;

      const sizeChanged =
        stateRef.current.width !== width || stateRef.current.height !== height;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (sizeChanged || stateRef.current.nodes.length === 0) {
        stateRef.current.width = width;
        stateRef.current.height = height;
        const nodeCount = getNodeCount(width, height);
        stateRef.current.nodes = Array.from({ length: nodeCount }, () => ({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * 0.35,
          vy: (Math.random() - 0.5) * 0.35,
          radius: Math.random() * 1.5 + 1,
        }));
      }
    };

    const draw = () => {
      const { width, height } = parent.getBoundingClientRect();
      const { nodes } = stateRef.current;

      if (width < 1 || height < 1 || nodes.length === 0) {
        animationId = requestAnimationFrame(draw);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      nodes.forEach((node) => {
        node.x += node.vx;
        node.y += node.vy;
        if (node.x < 0 || node.x > width) node.vx *= -1;
        if (node.y < 0 || node.y > height) node.vy *= -1;
      });

      for (let i = 0; i < nodes.length; i += 1) {
        for (let j = i + 1; j < nodes.length; j += 1) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.hypot(dx, dy);
          if (dist < CONNECTION_DISTANCE) {
            const alpha = 1 - dist / CONNECTION_DISTANCE;
            ctx.strokeStyle = `rgba(139, 92, 246, ${alpha * 0.22})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      nodes.forEach((node) => {
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = NODE_COLOR;
        ctx.fill();
      });

      animationId = requestAnimationFrame(draw);
    };

    resize();
    draw();

    const resizeObserver = new ResizeObserver(() => resize());
    resizeObserver.observe(parent);

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) resize();
      },
      { rootMargin: '100px 0px', threshold: 0 }
    );
    intersectionObserver.observe(parent);

    window.addEventListener('resize', resize);

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
}
